// src/modules/app-views/welcome/pages/Init/modals/FirstSubscriptionModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useCallback, useRef } from 'react';
import { RocketOutlined, LogoutOutlined, CloseOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import authService from 'services/authService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import stepOrganization from './StepOrganization';
import stepAiContext from './StepAiContext';
import stepResources from './StepResources';
import stepOperators from './StepOperators';
import stepFinish from './StepDone';
import accountService from 'modules/app-views/account/service';
import app from 'configs/appResolver';
import { onProduction } from 'utils/helpers';
import useSubscriptionLocale from 'hooks/useSubscriptionLocale';
import { countryCodeFromLocale } from 'components/SimpleComponents/SimpleEntityUid';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PREPARATION_DELAY_MS = 1000;
const FINISHING_DELAY_MS = 1000;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FirstSubscriptionModal = ({ show, setShow, afterClose, devMode = false }) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const storedUser = useZustand('auth', 'user');

  // 2. HOOKS & REFS
  const hasStartedCreationRef = useRef(false);
  const docStateRef = useRef(null);
  const { locale } = useSubscriptionLocale();
  const appCountryCode = countryCodeFromLocale(locale);

  // 3. LOCAL VARIABLES I
  const isProduction = onProduction();

  // 4. LOCAL STATE
  const [state, setState] = useState({ resources: buildInitialResources() });
  const [isPreparing, setIsPreparing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // 5. EFFECTS

  // 6. HANDLERS
  const handleExitSystem = useCallback(() => {
    authService.signOut({ replace: true });
  }, []);

  const executeCreation = useCallback(
    async ({ form }) => {
      const allValues = form?.getFieldsValue(true) || {};
      const effectiveCountryCode = allValues?.countryCode || appCountryCode;
      const effectiveIsPortugal = effectiveCountryCode === 'PT';

      setIsCreating(true);

      try {
        // 1. Build entity data - backend transforms individual fields into arrays
        const entityData = {
          entityType: 'company',
          entityProfile: ['company'],
          dataOrigin: allValues?.dataOrigin || 'external_api',
          _metadata: { origin: 0 },

          name: allValues?.name || allValues?.organization,
          fullName: allValues?.fullName || allValues?.name || allValues?.organization,
          firstName: allValues?.firstName,
          middleName: allValues?.middleName,
          lastName: allValues?.lastName,

          // Country-specific tax ID document
          ...(effectiveIsPortugal
            ? { nipc: allValues?.nipc }
            : { cnpj: allValues?.cnpj }),

          phone: allValues?.phone,
          email: allValues?.email,
          address: allValues?.address,
          foundedAt: allValues?.foundedAt,

          // Registration status from CNPJ API (Brazil only)
          registrationStatus: allValues?.registrationStatus,
          registrationStatusReason: allValues?.registrationStatusReason,

          // Activities (CNAE) from CNPJ API
          mainActivity: allValues?.mainActivity,
          secondaryActivities: allValues?.secondaryActivities,
          additionalActivities: allValues?.additionalActivities,

          legalNature: allValues?.legalNature,
          companySize: allValues?.companySize,
          shareCapital: allValues?.shareCapital,
          segment: allValues?.segment,

          // Holders/partners from CNPJ API
          holders: allValues?.holders,

          dataEnrichment: allValues?.dataEnrichment,
          fiscalData: allValues?.fiscalData,

          avatar: allValues?.avatar,
          backColor: allValues?.backColor,
          foreColor: allValues?.foreColor,

          countryCode: effectiveCountryCode,
        };

        const entityUrl = mergeUrlParamsQuery({ url: API.ENTITIES.URL, params: { _id: '' } });

        const [entityDoc] = await axiosService.post({
          url: entityUrl,
          data: entityData,
          returnAsObject: true,
        });

        const entityId = entityDoc?._id;

        // 2. Create subscription
        const subscriptionData = {
          organization: allValues?.organization || allValues?.name,
          profile: allValues?.profile || app.PRESET?.PROFILE || 'singleCompany',
          countryCode: effectiveCountryCode,
          entityId,
          aiBusinessContext: allValues?.aiBusinessContext,
          resources: state.resources,
          team: [
            {
              accountId: storedUser?._id,
              profile: 'owner',
              granted: {},
              invitedAt: new Date().toISOString(),
              invitedByAccountId: storedUser?._id,
              joinedAt: new Date().toISOString(),
              status: 2,
            },
          ],
        };

        const subscriptionUrl = mergeUrlParamsQuery({
          url: API.SUBSCRIPTIONS.URL,
          params: { _id: '' },
        });

        const [subscriptionDoc] = await axiosService.post({
          url: subscriptionUrl,
          data: subscriptionData,
          returnAsObject: true,
        });

        const newSubscriptionId = subscriptionDoc?._id;

        // 3. Replicate selected operators (appmulti only)
        const operatorIds = state?.operatorActiveIds || [];
        if (operatorIds.length > 0 && newSubscriptionId) {
          await axiosService.post({
            url: API.SYS_OPERATORS_API.REPLICATE,
            data: { seedEntityIds: operatorIds },
            headers: { 'x-subscriptionid': newSubscriptionId },
            timeout: 300000,
          });
        }

        // 4. Refresh auth
        await authService.signInRefresh();

        setIsCreating(false);
        setIsFinishing(true);

        await new Promise(resolve => setTimeout(resolve, FINISHING_DELAY_MS));

        setIsFinishing(false);
        setIsCompleted(true);
      } catch (error) {
        console.error('[FirstSubscriptionModal] Error creating subscription:', error);

        setIsCreating(false);
        setIsFinishing(false);
        setIsPreparing(false);
        hasStartedCreationRef.current = false;

        appService.notification({
          flag: 'error',
          title: 'error',
          description: 'error_creating_subscription',
        });
      }
    },
    [state.resources, state.operatorActiveIds, storedUser?._id, appCountryCode]
  );

  const handleEnterStepFinish = useCallback(
    async ({ form }) => {
      if (hasStartedCreationRef.current || isCompleted) return;
      hasStartedCreationRef.current = true;

      setIsPreparing(true);
      await new Promise(resolve => setTimeout(resolve, PREPARATION_DELAY_MS));
      setIsPreparing(false);
      await executeCreation({ form });
    },
    [isCompleted, executeCreation]
  );

  const handleFinish = async () => {
    if (isCompleted) {
      const nextStep = 'create_your_first_subscription';
      if (!accountService.hasNextStep(nextStep)) {
        await accountService.putNextStep(nextStep);
      }
      return true;
    }
    return false;
  };

  // 7. LOCAL VARIABLES II
  const isProcessing = isPreparing || isCreating || isFinishing;

  const steps = useMemo(
    () => [
      stepOrganization({ docStateRef, countryCode: appCountryCode }),
      stepAiContext(),
      stepOperators({ state, setState }),
      stepResources({ state, setState }),
      stepFinish({
        isPreparing,
        isCreating,
        isFinishing,
        isCompleted,
        onEnterStep: handleEnterStepFinish,
      }),
    ],
    [state, isPreparing, isCreating, isFinishing, isCompleted, handleEnterStepFinish, appCountryCode]
  );

  const showingExitButton = !devMode && !isProcessing && !isCompleted;
  const isClosable = showingExitButton ? false : !isProduction;

  const customLeftButtons = useMemo(() => {
    if (isPreparing || isCreating || isFinishing || isCompleted) return [];

    if (devMode) {
      return [
        {
          label: 'close',
          onClick: () => setShow(false),
          type: 'default',
          icon: <CloseOutlined />,
        },
      ];
    }

    return [
      {
        label: 'exit_system',
        onClick: handleExitSystem,
        type: 'default',
        icon: <LogoutOutlined />,
      },
    ];
  }, [handleExitSystem, isPreparing, isCreating, isFinishing, isCompleted, devMode, setShow]);

  // 8. UI
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<RocketOutlined />}
      title="welcome_first_subscription"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={640}
      maskClosable={false}
      closable={isClosable}
      customLeftButtons={customLeftButtons}
      disableBack={isProcessing || isCompleted}
      confirmDisabled={isProcessing}
      initialValues={{
        countryCode: appCountryCode,
        ...(app.PRESET?.PROFILE ? { profile: app.PRESET.PROFILE } : {}),
      }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FirstSubscriptionModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildInitialResources() {
  const native = app.PRESET?.NATIVE_RESOURCES || [];
  const suggested = app.PRESET?.SUGGESTED_RESOURCES || [];
  return [...new Set([...native, ...suggested])];
}
